import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Interface representing the pagination state.
 *
 * @interface PaginationState
 * @property {number} currentPage - The current active page number.
 * @property {number} rowsPerPage - The number of rows displayed per page.
 * @property {number[]} rowsPerPageOptions - Available options for the number of rows per page.
 * @property {number} totalItems - The total number of items available for pagination.
 */
interface PaginationState {
  currentPage: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  totalItems: number;
}

/**
 * The initial state for the pagination slice.
 *
 * @constant
 * @type {PaginationState}
 */
const initialState: PaginationState = {
  currentPage: 1,
  rowsPerPage: 25,
  rowsPerPageOptions: [5, 10, 25, 50, 100],
  totalItems: 0,
};

/**
 * A slice of the Redux store responsible for managing pagination state.
 *
 * This slice contains actions and reducers to control the current page,
 * the number of rows displayed per page, and the total number of items
 * for pagination purposes.
 *
 * @constant
 * @type {Slice<PaginationState>}
 */
const paginationSlice = createSlice({
  name: 'pagination',
  initialState,
  reducers: {
    /**
     * Sets the current page number.
     *
     * @param {PaginationState} state - The current state of the pagination slice.
     * @param {PayloadAction<number>} action - The action containing the new page number.
     */
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },

    /**
     * Sets the number of rows displayed per page.
     *
     * @param {PaginationState} state - The current state of the pagination slice.
     * @param {PayloadAction<number>} action - The action containing the new rows per page value.
     */
    setRowsPerPage(state, action: PayloadAction<number>) {
      state.rowsPerPage = action.payload;
    },

    /**
     * Sets the total number of items for pagination.
     *
     * @param {PaginationState} state - The current state of the pagination slice.
     * @param {PayloadAction<number>} action - The action containing the total number of items.
     */
    setTotalItems(state, action: PayloadAction<number>) {
      state.totalItems = action.payload;
    },
  },
});

/**
 * Action creators generated from the pagination slice.
 *
 * @constant
 * @type {{ setCurrentPage: (page: number) => PayloadAction<number>, setRowsPerPage: (rows: number) => PayloadAction<number>, setTotalItems: (total: number) => PayloadAction<number> }}
 */
export const { setCurrentPage, setRowsPerPage, setTotalItems } =
  paginationSlice.actions;

/**
 * The reducer for the pagination slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<PaginationState>}
 */
export default paginationSlice.reducer;
